import { useAuthStore } from '@stores/auth'
import postAuthRedirect from '@utils/post-auth-redirect'
import premiumFunnelOrigin from '@utils/premium-funnel-origin'
import { AUTH_ROUTES } from './auth-routes'

// The guards that read a store. Nuxt runs them as route middleware (app/middleware),
// named in each route's `meta.middleware`: a record's own `beforeEnter` would run in
// the router's first navigation, before @pinia/nuxt has created Pinia.

// These return `true` to let a navigation through, as vue-router guards do. Nuxt stops
// running a route's remaining middleware at the first one that returns anything, so
// "let through" has to be no result at all.
export function asRouteMiddleware(guard) {
    const letThrough = result => (result === true ? undefined : result)

    return (to, from) => {
        const result = guard(to, from)
        return result instanceof Promise ? result.then(letThrough) : letThrough(result)
    }
}

// The destination a signed-out visitor is bounced from is remembered by the
// global guard under `redirectURL`, which is what login reads on the way back —
// so this only has to decide whether to let them through.
export function requireAuth() {
    return useAuthStore().loggedIn || { name: 'login' }
}

export function requireAuthOrGoHome() {
    return useAuthStore().loggedIn || { name: 'home' }
}

export function redirectHomeIfLoggedIn() {
    return !useAuthStore().loggedIn || { name: 'home' }
}

// Nuxt's router lands the first page before any middleware exists, then replays that
// navigation from the page it landed on. The global middleware notes the first
// navigation it sees, so a guard can tell that replay, and any redirect it causes,
// from a later navigation that happens to share its path (a back to a duplicate entry).
let firstNavigation = null

export function noteFirstNavigation(routeTo) {
    firstNavigation ??= routeTo
}

function isStartUpReplay(routeTo, routeFrom) {
    const original = routeTo.redirectedFrom || routeTo
    return original === firstNavigation && routeFrom.fullPath === original.fullPath
}

// Where the funnel started belongs to the whole /premium cluster, not to the plans
// page: a visitor can enter at the welcome page, a deep-linked payment page, or
// donate. Middleware runs on every navigation, so this acts only when the previous
// route was outside the funnel, as a beforeEnter on the funnel's parent record did.
// That is what keeps a payment-page round trip from overwriting the origin.
export function rememberPremiumFunnelOrigin(routeTo, previousRoute) {
    const routeFrom = isStartUpReplay(routeTo, previousRoute) ? { name: undefined, matched: [] } : previousRoute

    const funnel = routeTo.matched[0]
    const isFunnel = record => (record.aliasOf || record) === (funnel.aliasOf || funnel)
    if (routeFrom.matched.some(isFunnel)) return true

    // A cold boot carrying the postAuth marker is a funnel continuation: auth
    // completed and sent the visitor back here, so the pre-auth origin must
    // survive. Otherwise an entry with no known origin drops any stale one,
    // and a return from the auth flow leaves the live origin alone.
    if (!routeTo.query.postAuth) {
        if (!routeFrom.name) premiumFunnelOrigin.clear()
        else if (!AUTH_ROUTES.includes(routeFrom.name)) premiumFunnelOrigin.set(routeFrom.fullPath)
    }
    return true
}

// Payment pages are strictly post-auth: a signed-out visitor (old link,
// bookmark, mid-funnel plan pick) is sent to sign-up first and returned to
// this exact payment page — plan id in the path, postAuth marker so the
// payment page can dissolve the funnel if they turn out to be Premium.
export function requireAuthForCheckout(routeTo) {
    if (useAuthStore().loggedIn) return true
    const joiner = routeTo.fullPath.includes('?') ? '&' : '?'
    postAuthRedirect.set(`${routeTo.fullPath}${joiner}postAuth=1`)
    return { name: 'signup' }
}

// /logout has no component. Returning nothing would allow the navigation and blank
// the page for the length of logOut()'s revoke request, so abort explicitly and let
// the reload logOut() performs do the navigating.
export function logOut() {
    useAuthStore().logOut()
    return false
}
